import React, { useRef, useEffect, useState, useCallback } from 'react';
import ProductItem from './ProductItem';
import { PRODUCTS_INITIAL_SHOW } from './config';

const ProductList = ({ products, selectedProductId, categoryId, onSelect, isVisible }) => {
  const [showAll, setShowAll] = useState(false);
  const [height, setHeight] = useState(0);
  const innerRef = useRef(null);

  const displayed = showAll ? products : products.slice(0, PRODUCTS_INITIAL_SHOW);
  const hiddenCount = products.length - PRODUCTS_INITIAL_SHOW;

  const recalcHeight = useCallback(() => {
    if (!innerRef.current) return;
    if (isVisible) {
      setHeight(innerRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isVisible]);

  // Recalculate when visibility, showAll or products change
  useEffect(() => {
    // Run immediately + after a paint cycle (for products that load async)
    recalcHeight();
    const id = setTimeout(recalcHeight, 50);
    return () => clearTimeout(id);
  }, [isVisible, showAll, products, recalcHeight]);

  // Use ResizeObserver to handle dynamic content height changes
  useEffect(() => {
    if (!innerRef.current) return;
    const observer = new ResizeObserver(() => {
      if (isVisible) {
        setHeight(innerRef.current ? innerRef.current.scrollHeight : 0);
      }
    });
    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Reset showAll when category collapses
  useEffect(() => {
    if (!isVisible) setShowAll(false);
  }, [isVisible]);

  const toggleShowAll = () => setShowAll((prev) => !prev);

  return (
    <div
      className="aptv2-product-list-outer"
      style={{
        maxHeight: isVisible ? `${Math.max(height, 1)}px` : '0px',
        overflow: 'hidden',
        transition: 'max-height 350ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div
        ref={innerRef}
        role="radiogroup"
        aria-label="Select a product"
        className="aptv2-product-list-inner"
      >
        {displayed.map((product) => (
          <ProductItem
            key={product.non_gp_product_id}
            product={product}
            isSelected={selectedProductId === product.non_gp_product_id.toString()}
            onSelect={onSelect}
          />
        ))}

        {hiddenCount > 0 && (
          <button
            className="aptv2-show-more-btn"
            onClick={toggleShowAll}
            type="button"
          >
            {showAll ? 'Show less' : `Show ${hiddenCount} more option${hiddenCount > 1 ? 's' : ''}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductList;
